import React, {   } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import EmailModal from './EmailModal';

/**
 * Specialiserad komponent för att skicka e-post till leverantörer
 * med obligatoriska mottagare som byggkonsult och inredare
 */
const LeverantorEpost = ({ 
  isOpen,
  onClose,
  supplier,
  projectName = '',
  projectId,
  projects = []
}) => {
  const { user } = useAuth();

  // Hitta projektansvariga (byggkonsult/inredare)
  const findProjectResponsibles = () => {
    // Samla ansvariga från alla projekt
    const responsibles = [];
    projects.forEach(project => {
      // Lägg till byggkonsult om den finns
      if (project.consultant && project.consultant.email) {
        responsibles.push({
          email: project.consultant.email,
          name: `${project.consultant.firstName} ${project.consultant.lastName}`,
          role: 'Byggkonsult',
          type: 'consultant',
          isMainRecipient: false,
          selected: true,
          isRequired: true // Markera denna som obligatorisk
        });
      }
      
      // Lägg till inredningsarkitekt om den finns
      if (project.architect && project.architect.email) {
        responsibles.push({
          email: project.architect.email,
          name: `${project.architect.firstName} ${project.architect.lastName}`,
          role: 'Inredningsarkitekt',
          type: 'architect',
          isMainRecipient: false,
          selected: true,
          isRequired: true // Markera denna som obligatorisk
        });
      }
    });
    
    // Ta bort eventuella dubletter (om någon är ansvarig för flera projekt)
    return responsibles.filter((responsible, index, self) => 
      index === self.findIndex(r => r.email === responsible.email)
    );
  };

  // Om ingen leverantör är vald
  if (!supplier) return null;

  // Hämta kontaktinformation från rätt objekt
  const getContactInfo = () => {
    // Försök hämta från primaryContactPerson först (det nya systemet)
    if (supplier.primaryContactPerson) {
      return {
        contactPerson: `${supplier.primaryContactPerson.firstName || ''} ${supplier.primaryContactPerson.lastName || ''}`.trim() || supplier.companyName,
        email: supplier.primaryContactPerson.email || supplier.email
      };
    }
    
    // Fallback till gamla systemet
    return {
      contactPerson: supplier.contactPerson || supplier.companyName,
      email: supplier.email
    };
  };

  const contactInfo = getContactInfo();

  // Skapa standardmeddelande för leverantörskontakt
  const getMessage = () => {
    // Basmeddelande med intressefrågor
    const baseMessage = `Hej,

Jag är intresserad av era produkter och tjänster, och skulle vilja veta mer om ert erbjudande med ${supplier.discountRate}% rabatt via Leadme.

Jag är särskilt intresserad av att få veta mer om:
- Vilka produkter som omfattas av rabatten
- Hur jag kan ta del av erbjudandet
- Eventuella ytterligare förmåner som ingår

Vänligen kontakta mig för att diskutera detta vidare.`;

    // Skapa signaturen med kontaktuppgifter om det är en kund
    const signature = `\n\nMed vänliga hälsningar,\n${user ? `${user.firstName} ${user.lastName}` : 'Kund'}`;
    
    // Extrahera projekt-ID från projektnamnet (format: Projektnamn #12345)
    let projectIdFromName = '';
    if (projectName) {
      const match = projectName.match(/#\d+/);
      if (match) {
        projectIdFromName = match[0];
      }
    }
    
    // Lägg till projekt-ID direkt efter namn
    const projectInfo = `\nProjekt ID: ${projectIdFromName || '#XXXXX'}`;
    
    // Lägg till kontaktuppgifter sist om det är en kund som skickar
    let contactInfo = '';
    if (user && user.role === 'kund') {
      // Lägg till en tomrad mellan projektID och kontaktuppgifter
      contactInfo += '\n';
      
      // Lägg till telefon först - kontrollera flera möjliga egenskapsnamn
      const phoneNumber = user.phoneNumber || user.phone || user.tel || user.telephone || '';
      if (phoneNumber && phoneNumber.trim() !== '') {
        contactInfo += `\nTel: ${phoneNumber}`;
      } else {
        // Om vi inte har något telefonnummer, lägg till en platshållare
        contactInfo += `\nTel: ej tillgängligt`;
      }
      
      // Lägg till e-post sist
      contactInfo += `\nEpost: ${user.email}`;
    }
    
    return baseMessage + signature + projectInfo + contactInfo;
  };

  // Säkerställ alltid definierade värden
  const subject = `Intresse för ${supplier.companyName}s erbjudande`;
  const message = getMessage();

  return (
    <EmailModal
      isOpen={isOpen}
      onClose={onClose}
      recipient={contactInfo.email}
      recipientName={contactInfo.contactPerson}
      senderName={user ? `${user.firstName} ${user.lastName}` : 'Kund'}
      recipientType="Leverantör"
      subject={subject}
      message={message}
      allRecipients={[
        // Leverantören som huvudmottagare
        {
          email: contactInfo.email, 
          name: contactInfo.contactPerson,
          role: 'Leverantör',
          type: 'supplier',
          isMainRecipient: true,
          selected: true
        },
        // Alla projektansvariga som övriga mottagare
        ...findProjectResponsibles()
      ]}
      showTeamMembers={true} // Visa alltid sektionen för övriga medlemmar
      projectName={projectName}
      projectId={projectId}
    />
  );
};

export default LeverantorEpost; 